import { html, LitElement, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { localized, msg, str } from "@lit/localize";
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

import styles from './usos-flow-step.scss';
import "../icon/usos-icon";

@customElement('usos-flow-step')
@localized()
export class UsosFlowStep extends LitElement {
    static override styles = styles;

    @property({attribute: 'top-label'})
    topLabel = '';

    @property({attribute: 'bottom-label'})
    bottomLabel = '';

    @property({attribute: 'step-number'})
    stepNumber = '';

    @property({reflect: true})
    status: 'done' | 'active' | 'future' = 'done';

    @property({reflect: true, attribute: 'open', type: Boolean})
    isOpen = false;

    @property({type: Boolean}) isLastStep = false;

    private internals: ElementInternals;

    constructor() {
        super();
        this.internals = this.attachInternals();
        this.internals.role = 'listitem';
    }

    override render() {
        const icon = {
            'done': 'check_circle',
            'active': 'pending',
            'future': 'block',
        }[this.status];
        return html`
            <button id="step" @click=${this.onClick} aria-hidden="true">${this.stepNumber}</button>
            <div id="status" aria-hidden="true">
                <usos-icon id="icon" icon-type="outlined" .iconName=${icon}></usos-icon>
                <div>
                    <div id="top-label">${this.topLabel}&nbsp;</div>
                    <div id="bottom-label">${this.bottomLabel}&nbsp;</div>
                </div>
            </div>
            <div id="line" aria-hidden="true"></div>
            ${when(!this.isLastStep, () => html`<div id="line-end" aria-hidden="true"></div>`)}
        `;
    }

    override updated(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('stepNumber')) {
            this.dispatchEvent(new CustomEvent('usos-flow:numberChanged', {
                bubbles: true,
                detail: { oldNumber: changedProperties.get('stepNumber') || '', newNumber: this.stepNumber }
            }));
        }
        const stepStatusLabel = {
            'done': msg('zakończony', { desc: 'Flow step status: done' }),
            'active': msg('bieżący', { desc: 'Flow step status: active' }),
            'future': msg('przyszły', { desc: 'Flow step status: future' }),
        }[this.status];
        this.internals.ariaLabel = `${msg('Krok', { desc: 'Flow step' })} ${this.stepNumber}, ${stepStatusLabel}: ${this.topLabel} — ${this.bottomLabel}`;
    }

    private onClick() {
        this.dispatchEvent(new CustomEvent('usos-flow:select', {
            bubbles: true,
            detail: { stepNumber: this.stepNumber }
        }));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "usos-flow-step": UsosFlowStep,
    }
}
